// ─────────────────────────────────────────────────────────────────
// Columns block — per-column widths
// ----------------------------------------------------------------
// A row (the `columns` block) is an equal split by default. One column can be
// pinned instead — a logo, an avatar, a price — and the others share what is
// left. The pin lives in `attrs.columnWidths`, index-aligned with
// `attrs.columnBlocks`, and is judged by `parseColumnWidth`
// (packages/shared/src/column-widths.ts), the same rule the theme renderer
// emits the email with.
// ─────────────────────────────────────────────────────────────────

import { COLUMN_WIDTH_PATTERN, EMAIL_CONTENT_WIDTH, parseColumnWidth } from '@blogforge/shared';
import { useState } from 'react';

/** A bare number is taken as pixels — `160` is what people type first. */
const BARE_NUMBER = /^\d+(?:\.\d+)?$/;

/** What one column's input says, and why it was refused when it was. */
interface ColumnDraft {
  text: string;
  error: string | null;
}

function draftOf(widths: (string | null)[], count: number): ColumnDraft[] {
  return Array.from({ length: count }, (_, i) => ({ text: widths[i] ?? '', error: null }));
}

/** Reads the field back: `null` means "fill", and anything unusable is refused. */
function resolve(text: string): { width: string | null } | { error: string } {
  const trimmed = text.trim();
  if (trimmed === '') return { width: null };

  const candidate = BARE_NUMBER.test(trimmed) ? `${trimmed}px` : trimmed;
  const width = parseColumnWidth(candidate, EMAIL_CONTENT_WIDTH);
  if (width) return { width };

  // Well-formed but out of range reads differently from a typo: one is a
  // layout that cannot work, the other is a word where a length belongs.
  const match = COLUMN_WIDTH_PATTERN.exec(candidate);
  if (match) {
    return {
      error:
        match[2] === '%'
          ? 'A column can be pinned to less than 100%, or left blank to fill the row.'
          : `A column cannot be ${EMAIL_CONTENT_WIDTH}px or wider — the columns that fill would have nothing left.`,
    };
  }
  return { error: 'Use a width like 160px or 30%, or leave it blank to fill the row.' };
}

interface ColumnWidthsFieldProps {
  /** `attrs.columnWidths` as stored (index-aligned, `null` = fill). */
  columnWidths: (string | null)[];
  /** `attrs.columns`, so a row always shows one field per column. */
  colCount: number;
  /** Persists the whole array — the block is replaced, not patched. */
  onCommit: (next: (string | null)[]) => void;
}

/**
 * One text field per column. A pinned row is written on commit (blur or
 * Enter) rather than on every keystroke: a half-typed `16` would otherwise be
 * a real 16px column on the canvas, and a rejected value has to stay on screen
 * to be corrected.
 */
export function ColumnWidthsField({ columnWidths, colCount, onCommit }: ColumnWidthsFieldProps) {
  const [drafts, setDrafts] = useState<ColumnDraft[]>(() => draftOf(columnWidths, colCount));

  const commit = (index: number) => {
    const draft = drafts[index];
    if (!draft) return;

    const trimmed = draft.text.trim();
    const current = columnWidths[index] ?? '';
    // Writing through on every blur would churn the block (and the undo stack)
    // when nothing was typed.
    if (trimmed === current) return;

    const resolved = resolve(trimmed);
    if ('error' in resolved) {
      setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, error: resolved.error } : d)));
      return;
    }

    const next = Array.from({ length: colCount }, (_, i) => columnWidths[i] ?? null);
    next[index] = resolved.width;
    setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, error: null } : d)));
    onCommit(next);
  };

  const setText = (index: number, text: string) =>
    setDrafts((prev) => prev.map((d, i) => (i === index ? { text, error: null } : d)));

  // What the row adds up to: a pinned row that leaves nothing (or less) for
  // the columns that fill is not invalid, just not what the pin is for.
  const usedPx = Array.from({ length: colCount }, (_, i) =>
    parseColumnWidth(columnWidths[i], EMAIL_CONTENT_WIDTH),
  ).reduce((total, width) => {
    if (!width) return total;
    return (
      total +
      (width.endsWith('%')
        ? (Number(width.slice(0, -1)) / 100) * EMAIL_CONTENT_WIDTH
        : Number(width.slice(0, -2)))
    );
  }, 0);

  return (
    <div>
      <label className="field-label">Column widths</label>
      <div className="space-y-1">
        {drafts.map((draft, index) => (
          <div key={index}>
            <div className="flex items-center gap-1.5">
              <span className="text-muted w-16 shrink-0 text-xs">Column {index + 1}</span>
              <input
                className="field-input px-2 py-1 font-mono text-xs"
                placeholder="fill"
                aria-label={`Width of column ${index + 1}`}
                value={draft.text}
                onChange={(e) => setText(index, e.target.value)}
                onBlur={() => commit(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commit(index);
                  }
                }}
              />
            </div>
            {draft.error && <p className="text-danger mt-1 pl-[4.4rem] text-xs">{draft.error}</p>}
          </div>
        ))}
      </div>
      <p className="text-muted mt-1 text-xs">
        A length (160px or 30%) pins that column and the columns left blank share the rest — for a
        logo beside its copy. Blank columns split the row evenly.
      </p>
      {usedPx >= EMAIL_CONTENT_WIDTH && (
        <p className="text-warn mt-1 text-xs">
          Fixed columns already take {Math.round(usedPx)}px of the {EMAIL_CONTENT_WIDTH}px content
          width — once the gaps are added there may be nothing left for the columns that fill.
        </p>
      )}
    </div>
  );
}
